import React, { useState } from "react";
import { TrendingUp } from "lucide-react";
import { ImpactSlide } from "../../types/impactSlides";

interface ImpactSlideshowProps {
  slides: ImpactSlide[];
}

// Longer statements stay up longer so they can actually be read.
const slideDuration = (slide: ImpactSlide) =>
  slide.type === "text"
    ? Math.min(8000, Math.max(3500, 2000 + slide.text.trim().split(/\s+/).length * 280))
    : 4200;

const ImpactSlideshow: React.FC<ImpactSlideshowProps> = ({ slides }) => {
  const [active, setActive] = useState(0);
  const [leaving, setLeaving] = useState<number | null>(null);

  // Advancing is driven by the progress bar's animationend, so pausing the bar
  // (hover/focus) pauses the whole show.
  const advance = () => {
    setLeaving(active);
    setActive((active + 1) % slides.length);
  };

  const statements = slides.flatMap((slide) => (slide.type === "text" ? [slide.text.trim()] : []));

  return (
    <div className="impact-show">
      <div className="impact-show__stage" aria-hidden="true">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={[
              "impact-show__slide",
              `impact-show__slide--${slide.type}`,
              index === active && "is-active",
              index === leaving && "is-leaving",
            ]
              .filter(Boolean)
              .join(" ")}
            style={{ "--slide-duration": `${slideDuration(slide)}ms` } as React.CSSProperties}
          >
            {slide.type === "image" ? (
              <img src={slide.imageUrl} alt="" className={index % 2 ? "is-reverse" : undefined} />
            ) : (
              <div className="impact-show__statement">
                <span className="impact-show__label">
                  <TrendingUp size={14} /> Impact
                </span>
                <p>
                  {slide.text
                    .trim()
                    .split(/\s+/)
                    .map((word, wordIndex) => (
                      <span
                        key={wordIndex}
                        className={`impact-show__word${/\d/.test(word) ? " is-figure" : ""}`}
                        style={{ "--i": wordIndex } as React.CSSProperties}
                      >
                        {word}{" "}
                      </span>
                    ))}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {slides.length > 1 && (
        <div className="impact-show__progress" aria-hidden="true">
          {slides.map((slide, index) => (
            <span key={slide.id} className={index < active ? "is-done" : index === active ? "is-active" : undefined}>
              <span
                style={{ animationDuration: `${slideDuration(slide)}ms` }}
                onAnimationEnd={index === active ? advance : undefined}
              />
            </span>
          ))}
        </div>
      )}

      {statements.length > 0 && <span className="sr-only">Impact: {statements.join(". ")}</span>}
    </div>
  );
};

export default ImpactSlideshow;
